import {AdLoadInfo, AdMob, AdmobConsentStatus, AdMobError, InterstitialAdPluginEvents} from '@capacitor-community/admob';
import {Capacitor} from "@capacitor/core";
import {createContext, PropsWithChildren, useContext, useEffect} from "react";
import {config} from "../config/config.ts";
import {useJwtUser} from "../JWTAuthProvider.tsx";

const GlobalAdmobContext = createContext<{
    interstitialShow: any,
} | null>(null);

export const GlobalAdmobProvider = ({children}: PropsWithChildren) => {
    const user = useJwtUser();

    if (!config.isCordova || localStorage.disable_ads || Capacitor.getPlatform() !== 'android') {
        return <GlobalAdmobContext.Provider value={null}>
            {children}
        </GlobalAdmobContext.Provider>
    }

    const platformId = Capacitor.getPlatform();
    const AD_TIMEOUT = (config.ads[platformId]?.delaySeconds || 180) * 1_000;

    if (!localStorage.lastAdTs) {
        localStorage.lastAdTs = +new Date();
    }

    function prepareInterstitial() {
        return AdMob.prepareInterstitial({
            adId:      config.ads[platformId].interstitial,
            isTesting: false,
            // npa: true
        });
    }

    useEffect(() => {
        ;(async () => {
            console.debug("ADMOB_INIT", 'start')

            await AdMob.initialize({initializeForTesting: false});

            AdMob.addListener(InterstitialAdPluginEvents.Loaded, (info: AdLoadInfo) => {
                console.log("[InterstitialAdPluginEvents.Loaded] ADMOB_INIT info", info)
            });
            AdMob.addListener(InterstitialAdPluginEvents.Showed, () => {
                console.log("[InterstitialAdPluginEvents.Showed] ADMOB_INIT")
            });
            AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
                console.log("[InterstitialAdPluginEvents.Dismissed] ADMOB_INIT info")

                localStorage.lastAdTs = +new Date();

                prepareInterstitial();
            });
            AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, (error: AdMobError) => {
                console.log("[InterstitialAdPluginEvents.Dismissed] ADMOB_INIT info", error)

                localStorage.lastAdTs = +new Date();

                prepareInterstitial();
            });

            console.log("ADMOB_INIT", 'added listeners')

            const [trackingInfo, consentInfo] = await Promise.all([
                AdMob.trackingAuthorizationStatus(),
                AdMob.requestConsentInfo(),
            ]);

            console.log("ADMOB_INIT", 'trackingInfo', trackingInfo)

            if (trackingInfo.status === 'notDetermined') {
                await AdMob.requestTrackingAuthorization();
            }

            const authorizationStatus = await AdMob.trackingAuthorizationStatus();
            console.log("ADMOB_INIT", 'authorizationStatus', authorizationStatus)

            if (
                authorizationStatus.status === 'authorized' &&
                consentInfo.isConsentFormAvailable &&
                consentInfo.status === AdmobConsentStatus.REQUIRED
            ) {
                console.log("ADMOB_INIT", 'showConsentForm')

                await AdMob.showConsentForm();
            }

            console.log("ADMOB_INIT", 'done')

        })()
    }, [])

    function canShowAd() {
        console.log('canShowAd', JSON.stringify([+new Date(), localStorage.lastAdTs, +new Date() - +localStorage.lastAdTs]), 'subscribed', JSON.stringify([user === null, user?.is_subscribed, user?.meta?.disable_ads]));
        if (!localStorage.lastAdTs || +new Date() - +localStorage.lastAdTs < AD_TIMEOUT) {
            return false;
        }
        localStorage.lastAdTs = +new Date();

        // check user flags
        if (user === null || user?.is_subscribed || user?.meta?.disable_ads) {
            return false;
        }
        return true;
    }

    async function interstitialShow(force = false) {
        if (!canShowAd() && !force) {
            return;
        }

        await prepareInterstitial();
        await AdMob.showInterstitial();
    }

    async function onResume() {
        console.log('onResume', 'user', JSON.stringify(user?.email));

        // NOTE `resume` event is triggered when dismissing interstitial ads or by other reasons,
        // make sure to add logic to control when to display the ad.
        interstitialShow();
    }

    useEffect(() => {
        document.addEventListener('resume', onResume, false)

        return () => {
            document.removeEventListener('resume', onResume)
        }
    });

    return (
        <GlobalAdmobContext.Provider
            value={{
                interstitialShow,
            }}
        >
            {children}
        </GlobalAdmobContext.Provider>
    );
};

export function useGlobalAdmob() {
    const context = useContext(GlobalAdmobContext);
    const noop = async () => { // AdMob requires mobile
    };
    if (context !== null) {
        return {
            interstitialShow: context.interstitialShow,
        };
    }
    return {interstitialShow: noop};
}
